import { Column_ColumnVisibility, RowData, Table, TableFeatures } from "@tanstack/react-table";

export interface ColumnGroupBoundaries {
  groupEndIds: Set<string>;
  groupStartIds: Set<string>;
}

export const EMPTY_COLUMN_GROUP_BOUNDARIES: ColumnGroupBoundaries = {
  groupEndIds: new Set<string>(),
  groupStartIds: new Set<string>(),
};

/**
 * Identifies which leaf columns sit at the start/end of a multi-leaf column group (e.g. a
 * columnHelper.group() spanning several leaves), so VirtualizedTableHeader/Row/RowSkeleton can
 * draw a vertical divider marking each group's boundaries — one border-left on the first visible
 * leaf, one border-right on the last, so the line runs from the group header down through every
 * body row without doubling up with an adjacent cell's own border.
 *
 * Reads from the live `table` (not the static column defs) so the boundary follows whichever
 * leaves are currently *visible* — hiding a group's first column via the visibility picker shifts
 * the start marker to the new first-visible leaf, rather than leaving it on a hidden column.
 *
 * A column with only one leaf under it (an ungrouped top-level column, which TanStack still models
 * as an entry in the group-header row) isn't a meaningful group boundary and is excluded from both
 * sets.
 *
 * The `as typeof column & Column_ColumnVisibility` cast below is load-bearing — see
 * VirtualizedTableHeader's doc comment for why an abstract `TFeatures` type parameter can't resolve
 * feature-contributed methods (here, `getIsVisible`) on its own.
 */
export const getColumnGroupBoundaries = <TFeatures extends TableFeatures, TData extends RowData>(
  table: Table<TFeatures, TData>,
): ColumnGroupBoundaries => {
  const headerGroups = table.getHeaderGroups();
  if (headerGroups.length < 2) {
    return EMPTY_COLUMN_GROUP_BOUNDARIES;
  }

  const groupStartIds = new Set<string>();
  const groupEndIds = new Set<string>();

  headerGroups[0].headers.forEach((header) => {
    // getLeafColumns() returns every leaf under this column regardless of visibility, so a
    // group's originally-first/last leaf could be a currently-hidden one — filter to visible
    // leaves before picking the boundary. If exactly one visible leaf remains, it gets both
    // classes (fully boxed) rather than losing its boundary markers entirely.
    const allLeafColumns = header.column.getLeafColumns();
    if (allLeafColumns.length <= 1) {
      return;
    }
    const visibleLeafColumns = allLeafColumns.filter((column) =>
      (column as typeof column & Column_ColumnVisibility).getIsVisible(),
    );
    if (visibleLeafColumns.length === 0) {
      return;
    }
    groupStartIds.add(visibleLeafColumns[0].id);
    groupEndIds.add(visibleLeafColumns[visibleLeafColumns.length - 1].id);
  });

  return { groupEndIds, groupStartIds };
};
